import { FC } from 'react';
import { Box, Divider, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';
import { CheckCircleIcon, ClockCountdownIcon } from '@phosphor-icons/react';

interface AssessmentSummaryCardProps {
  evidenceDocumentsCount: number;
  minEstimatedTime: number;
  maxEstimatedTime: number;
}

export const AssessmentSummaryCard: FC<AssessmentSummaryCardProps> = observer(
  ({ evidenceDocumentsCount, minEstimatedTime, maxEstimatedTime }) => {
    const theme = useTheme();

    return (
      <Paper
        elevation={1}
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
            <ClockCountdownIcon color={theme.palette.black[300]} size={20} />
            <Typography variant="textMD_semiBold">
              <Trans i18nKey="assessments.new.summary.title" />
            </Typography>
          </Box>
          <Typography variant="textSM_regular" sx={{ color: 'text.tertiary', mt: 1, mb: 1 }}>
            <Trans i18nKey="assessments.new.summary.description" />
          </Typography>
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              borderRadius: 4,
              backgroundColor: 'grey.50',
              mt: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 2,
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Typography variant="displaySM_regular" sx={{ color: 'blue.700' }}>
                  {evidenceDocumentsCount}
                </Typography>
                <Typography
                  variant="textXS_medium"
                  sx={{ fontSize: '0.75rem', color: 'text.tertiary', textAlign: 'center', mt: 0.5 }}
                >
                  <Trans i18nKey="assessments.new.summary.questions" />
                </Typography>
              </Box>
              <Divider sx={{ width: '100%', my: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="displaySM_regular"
                  sx={{
                    color: 'error.main',
                  }}
                >
                  <Trans i18nKey="assessments.new.summary.traditionalTime" />
                </Typography>
                <Typography variant="textXS_medium" sx={{ color: 'text.tertiary' }}>
                  <Trans i18nKey="assessments.new.summary.traditionalReview" />
                </Typography>
              </Box>
              <Divider sx={{ width: '100%', my: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="displaySM_regular"
                  sx={{
                    color: 'blue.700',
                  }}
                >
                  <Trans i18nKey="assessments.new.summary.aiTime" />
                </Typography>
                <Typography
                  variant="textXS_medium"
                  sx={{ color: 'text.tertiary', textAlign: 'center' }}
                >
                  <Trans i18nKey="assessments.new.summary.aiTimeDescription" />
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Typography variant="textMD_semiBold" sx={{ mt: 3, mb: 1.5, display: 'block' }}>
            <Trans i18nKey="assessments.new.summary.optimizedTitle" />
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 1.5,
              mt: 1.5,
            }}
          >
            <Box sx={{ flexShrink: 0, pt: 0.25 }}>
              <CheckCircleIcon color={theme.palette.accent.green} size={20} weight="bold" />
            </Box>
            <Typography variant="textSM_regular">
              <Trans i18nKey="assessments.new.summary.optimizedTip1" />
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 1.5,
              mt: 1.5,
            }}
          >
            <Box sx={{ flexShrink: 0, pt: 0.25 }}>
              <CheckCircleIcon color={theme.palette.accent.green} size={20} weight="bold" />
            </Box>
            <Typography variant="textSM_regular">
              <Trans i18nKey="assessments.new.summary.optimizedTip2" />
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 1.5,
              mt: 1.5,
            }}
          >
            <Box sx={{ flexShrink: 0, pt: 0.25 }}>
              <CheckCircleIcon color={theme.palette.accent.green} size={20} weight="bold" />
            </Box>
            <Typography variant="textSM_regular">
              <Trans i18nKey="assessments.new.summary.optimizedTip3" />
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  },
);
